import { useEffect, useState } from 'react';
import { useTeammate } from '@/hooks/useTeammate';
import { AuthStore } from '@/state/AuthenticationStore';
import { teamMateStore } from '@/state/TeammateStore';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { Invitation, TeamMate } from '@/types/teammate.types';
import InvitesTable from '../Components/InvitesTable';
import TeammatesTable from '../Components/TeammatesTable';
import { Search, Plus, RefreshCw, Users, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { getWorkspaceCreatorId } from '@/utils';
import { useDebounceValue } from '@/hooks/useDebounce';
import Logger from '@/config/Logger';

const Teammates = () => {
  const [searchTeammates, setSearchTeammates] = useState('');
  const [searchInvite, setSearchInvite] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('teammates');
  const [filteredTeamMates, setFilteredTeamMates] = useState<TeamMate[]>([]);
  const [filteredInvitations, setFilteredInvitations] = useState<Invitation[]>(
    [],
  );
  const debouncedSearchTeammates = useDebounceValue(searchTeammates, 500);
  const debouncedSearchInvite = useDebounceValue(searchInvite, 500);
  const {
    getTeamMates,
    getInvites,
    resendInvite,
    deleteInvite,
    deleteTeamMate,
  } = useTeammate();
  const fetchingInvites = teamMateStore?.fetchingInvites;
  const fetchingTeamMates = teamMateStore?.fetchingTeamMates;
  const invitations = teamMateStore?.invites;
  const team_mates = teamMateStore?.teamMates;

  const navigate = useNavigate();

  const handleSubmitTeammates = (e: any, search: string) => {
    e.preventDefault();
    setSearchTeammates(search); // remove search from the input
  };
  const handleSubmitInvites = (e: any, search: string) => {
    e.preventDefault();
    setSearchInvite(search); // remove search from the input
  };

  const handleSearchTeammates = (e: any) => {
    const search = e.target.value;
    setSearchTeammates(search);
  };

  const handleSearchInvites = (e: any) => {
    const search = e.target.value;
    setSearchInvite(search);
  };

  // Debounced teammates search effect
  useEffect(() => {
    const fetchTeammates = async () => {
      try {
        await getTeamMates(debouncedSearchTeammates.trim());
        setFilteredTeamMates(teamMateStore.teamMates);
      } catch (error) {
        Logger.error('Failed to search teammates', error);
      }
    };
    fetchTeammates();
  }, [debouncedSearchTeammates]);

  // Debounced invites search effect
  useEffect(() => {
    const fetchInvites = async () => {
      try {
        await getInvites(debouncedSearchInvite.trim());
        setFilteredInvitations(teamMateStore.invites);
      } catch (error) {
        Logger.error('Failed to search invites', error);
      }
    };
    fetchInvites();
  }, [debouncedSearchInvite]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await getTeamMates();
    await getInvites();
    setIsRefreshing(false);
  };

  const workspace_name =
    AuthStore.user_workspace_info?.active_workspace?.workspace?.company_name;
  const created_by = getWorkspaceCreatorId(AuthStore);
  const user_id = AuthStore?.currentUser?._id;

  useEffect(() => {
    getTeamMates();
    getInvites();
  }, []);

  useEffect(() => {
    setFilteredTeamMates(team_mates);
  }, [team_mates?.length]);

  useEffect(() => {
    setFilteredInvitations(invitations);
  }, [invitations?.length]);

  return (
    <div className='flex flex-col w-full min-h-full overflow-hidden bg-white'>
      <div className='px-6 py-8'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl font-semibold text-gray-900'>Teammates</h1>
            <p className='text-lg text-gray-500 mt-1'>for {workspace_name}</p>
          </div>
          <div className='flex items-center space-x-3'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleRefresh}
              disabled={isRefreshing}
              className='flex items-center space-x-2'
            >
              <RefreshCw
                className={cn('w-4 h-4', isRefreshing && 'animate-spin')}
              />
              <span>Refresh</span>
            </Button>
            <Button
              size='sm'
              className='bg-secondary text-white hover:bg-secondary/80 flex items-center space-x-2'
              onClick={() => navigate('invites')}
              disabled={created_by !== user_id}
            >
              <Plus className='w-4 h-4' />
              <span>Add new teammates</span>
            </Button>
          </div>
        </div>

        <div className='py-4 space-y-4'>
          <div className='flex items-center justify-between'>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className='w-auto'
            >
              <TabsList className='grid w-fit grid-cols-2'>
                <TabsTrigger
                  value='teammates'
                  className='flex items-center space-x-2 text-md'
                >
                  <Users className='w-4 h-4' />
                  <span>Teammates ({team_mates?.length})</span>
                </TabsTrigger>
                <TabsTrigger
                  value='invites'
                  className='flex items-center space-x-2 text-md'
                >
                  <UserPlus className='w-4 h-4' />
                  <span>Invites sent ({invitations?.length})</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {activeTab === 'teammates' ? (
              <div className='relative w-80'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                <Input
                  placeholder='Search teammates...'
                  value={searchTeammates}
                  type='search'
                  onChange={handleSearchTeammates}
                  onKeyDown={e => {
                    if (e.code === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (searchTeammates.trim() !== '') {
                        handleSubmitTeammates(e, searchTeammates);
                      }
                    }
                  }}
                  className='pl-10 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
                />
              </div>
            ) : (
              <div className='relative w-80'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                <Input
                  placeholder='Search invites...'
                  value={searchInvite}
                  type='search'
                  onChange={handleSearchInvites}
                  onKeyDown={e => {
                    if (e.code === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (searchInvite.trim() !== '') {
                        handleSubmitInvites(e, searchInvite);
                      }
                    }
                  }}
                  className='pl-10 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='overflow-hidden h-full px-6 pb-4'>
        {activeTab === 'teammates' ? (
          <TeammatesTable
            team_mates={filteredTeamMates}
            loading={fetchingTeamMates}
            deleteTeammate={deleteTeamMate}
          />
        ) : (
          <InvitesTable
            invitations={filteredInvitations}
            loading={fetchingInvites}
            handleResendVerificationEmail={resendInvite}
            deleteInvite={deleteInvite}
          />
        )}
      </div>
    </div>
  );
};
export default observer(Teammates);
